import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Alert,
  AlertTitle,
  Divider,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SecurityIcon from '@mui/icons-material/Security';
import BackupIcon from '@mui/icons-material/Backup';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PolicyIcon from '@mui/icons-material/Policy';
import ExtensionIcon from '@mui/icons-material/Extension';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import BusinessIcon from '@mui/icons-material/Business';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import ApiIcon from '@mui/icons-material/Api';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import CachedIcon from '@mui/icons-material/Cached';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import SyncIcon from '@mui/icons-material/Sync';

const BestPracticesTab: React.FC = () => {
  return (
    <Box sx={{ mb: 4 }}>
      {/* Introduction Section - Similar to WorkflowsTab */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'success.lighter', borderLeft: '5px solid', borderColor: 'success.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <LightbulbIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'success.main' }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'success.dark' }}>
            Best Practices
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          Comprehensive <strong>best practice guidelines</strong> for managing Microsoft Entra ID applications 
          securely and efficiently using AppConfig²'s advanced features and enterprise-grade workflows.
        </Typography>
        <Typography variant="body2" color="text.primary">
          These recommendations are based on real-world enterprise deployments and Microsoft's official 
          guidance. Following these practices will help you maintain secure, scalable, and maintainable 
          identity configurations across your organization.
        </Typography>
      </Paper>

      {/* Quick Overview Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: '#f0f7ff', borderLeft: '5px solid', borderColor: 'info.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <VerifiedUserIcon sx={{ fontSize: '1.5rem', mr: 1, color: 'info.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'info.dark' }}>
            Key Areas Covered
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              🔒 <strong>Security Best Practices</strong> - Principle of least privilege and credential management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              🏗️ <strong>Application Lifecycle Management</strong> - Planning, configuration, and governance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              🔧 <strong>Development & Testing</strong> - Environment management and token validation
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              📊 <strong>Monitoring & Maintenance</strong> - Proactive monitoring and performance optimization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              🔌 <strong>Directory Extensions</strong> - Schema design and Entra Connect considerations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              👥 <strong>Team Collaboration</strong> - Documentation standards and change management
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Content Divider */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mt: 3, mb: 3, borderBottom: '2px solid', borderColor: 'success.light', pb: 1 }}>
        Enterprise Best Practices
      </Typography>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Enterprise-Grade Application Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Follow these comprehensive best practices to ensure secure, maintainable, and efficient 
            management of your Microsoft Entra ID applications using AppConfig².
          </Typography>
        </CardContent>
      </Card>

      {/* Security Best Practices */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SecurityIcon sx={{ mr: 1, color: '#d32f2f', fontSize: '2rem' }} />
            <Typography variant="h6" color="#d32f2f">Security Best Practices</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(244, 67, 54, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#d32f2f' }}>
                  Principle of Least Privilege
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Grant minimum required permissions"
                      secondary="Only assign permissions that applications actually need to function"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Regular permission audits"
                      secondary="Periodically review and remove unnecessary permissions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Use delegated permissions when possible"
                      secondary="Prefer user context over application context for better security"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Implement conditional access"
                      secondary="Use CA policies to add security layers based on context"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(244, 67, 54, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#d32f2f' }}>
                  Credential Management
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Regular credential rotation"
                      secondary="Rotate client secrets and certificates every 6-12 months"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Use certificates over secrets"
                      secondary="Certificates provide better security than client secrets"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Secure credential storage"
                      secondary="Store secrets in Azure Key Vault or similar secure services"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="error" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Monitor credential usage"
                      secondary="Track when and how credentials are being used"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>

          <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
            <AlertTitle>Critical Security Considerations</AlertTitle>
            <Typography variant="body2">
              • Never store client secrets in source code or configuration files<br/>
              • Use specific redirect URIs - avoid wildcards or overly broad patterns<br/>
              • Implement proper CORS policies for browser-based applications<br/>
              • Enable sign-in logs and regularly monitor for suspicious activity
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Application Management */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ManageAccountsIcon sx={{ mr: 1, color: '#1976d2', fontSize: '2rem' }} />
            <Typography variant="h6" color="#1976d2">Application Lifecycle Management</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Planning & Design
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Define clear naming conventions"
                      secondary="Use consistent, descriptive names across all applications"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Plan application architecture"
                      secondary="Design with security, scalability, and maintainability in mind"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Document application purpose"
                      secondary="Maintain clear documentation of what each app does"
                    />
                  </ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label="Naming Conventions" variant="outlined" />
                  <Chip size="small" label="Documentation" variant="outlined" />
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Configuration Management
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Use infrastructure as code"
                      secondary="Automate application deployment and configuration"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Implement configuration drift detection"
                      secondary="Monitor and alert on unexpected configuration changes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Version control manifests"
                      secondary="Store application manifests in source control"
                    />
                  </ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label="IaC" variant="outlined" />
                  <Chip size="small" label="Version Control" variant="outlined" />
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1976d2' }}>
                  Ownership & Governance
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Assign clear ownership"
                      secondary="Each application should have designated owners"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Implement approval workflows"
                      secondary="Require approval for sensitive configuration changes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Regular ownership reviews"
                      secondary="Periodically verify and update application owners"
                    />
                  </ListItem>
                </List>
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label="Ownership" variant="outlined" />
                  <Chip size="small" label="Governance" variant="outlined" />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Development & Testing */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <CodeIcon sx={{ mr: 1, color: '#388e3c', fontSize: '2rem' }} />
            <Typography variant="h6" color="#388e3c">Development & Testing Best Practices</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#388e3c' }}>
                  Environment Management
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Separate development environments"
                      secondary="Use distinct tenants or app registrations for dev/test/prod"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test in development first"
                      secondary="Always validate authentication flows in non-production environments"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Use AppConfig² for testing"
                      secondary="Leverage built-in testing tools to validate configurations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Automate testing workflows"
                      secondary="Include authentication testing in CI/CD pipelines"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#388e3c' }}>
                  Token Validation
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Verify token contents"
                      secondary="Check that tokens contain expected claims and values"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test token expiration"
                      secondary="Verify proper handling of expired tokens"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Validate audience claims"
                      secondary="Ensure tokens are issued for the correct audience"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test different user contexts"
                      secondary="Verify behavior with various user types and permissions"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Monitoring & Maintenance */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <MonitorHeartIcon sx={{ mr: 1, color: '#ff9800', fontSize: '2rem' }} />
            <Typography variant="h6" color="#ef6c00">Monitoring & Maintenance</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#ef6c00' }}>
                  Proactive Monitoring
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Monitor application usage"
                      secondary="Track authentication patterns and identify anomalies"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Set up alerting"
                      secondary="Configure alerts for failed authentications and errors"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Review sign-in logs"
                      secondary="Regularly analyze Entra ID sign-in logs for issues"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#ef6c00' }}>
                  Regular Maintenance
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Credential expiration tracking"
                      secondary="Monitor and rotate credentials before expiration"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Permission audits"
                      secondary="Quarterly reviews of application permissions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Cleanup unused applications"
                      secondary="Remove applications that are no longer needed"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#ef6c00' }}>
                  Performance Optimization
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Optimize token lifetime"
                      secondary="Balance security with user experience"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Cache tokens appropriately"
                      secondary="Implement proper token caching strategies"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="warning" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Monitor performance metrics"
                      secondary="Track authentication latency and success rates"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Directory Extensions Best Practices */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ExtensionIcon sx={{ mr: 1, color: '#7b1fa2', fontSize: '2rem' }} />
            <Typography variant="h6" color="#7b1fa2">Directory Extensions Best Practices</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(156, 39, 176, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#7b1fa2' }}>
                  Schema Design
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Use a dedicated application"
                      secondary="Create a specific application to own all your custom extensions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Clear naming convention"
                      secondary="Use consistent and descriptive names for extension properties"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Limit extension count"
                      secondary="Keep the number of extensions reasonable to maintain performance"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Document extension purpose"
                      secondary="Maintain clear documentation of what each extension stores"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(156, 39, 176, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#7b1fa2' }}>
                  Entra Connect Considerations
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Prefer Entra Connect for user attributes"
                      secondary="Use on-premises AD synchronization when possible"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Plan for hybrid scenarios"
                      secondary="Consider both cloud-only and synchronized attributes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test claims mapping"
                      secondary="Verify extensions appear correctly in tokens"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="secondary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Monitor synchronization"
                      secondary="Ensure Entra Connect is working properly"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Backup & Recovery */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <BackupIcon sx={{ mr: 1, color: '#00796b', fontSize: '2rem' }} />
            <Typography variant="h6" color="#004d40">Backup & Disaster Recovery</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(0, 121, 107, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#004d40' }}>
                  Regular Backups
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#00796b' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Automated backup schedule"
                      secondary="Use AppConfig² backup feature on a regular schedule"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#00796b' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Version control integration"
                      secondary="Store backup files in your source control system"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#00796b' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Pre-change backups"
                      secondary="Always backup before making significant changes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#00796b' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Backup validation"
                      secondary="Periodically test that backups can be restored"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'rgba(0, 121, 107, 0.05)', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#004d40' }}>
                  Recovery Planning
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#00796b' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Document recovery procedures"
                      secondary="Maintain clear procedures for restoring applications"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#00796b' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Test recovery scenarios"
                      secondary="Practice restoring applications in test environments"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#00796b' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Cross-tenant recovery"
                      secondary="Plan for scenarios where you need to recreate apps in different tenants"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#00796b' }} fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Secret recreation"
                      secondary="Remember that secrets cannot be backed up and must be recreated"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Team Collaboration */}
      <Card sx={{ borderRadius: 2, bgcolor: '#f8f9fc' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <GroupWorkIcon sx={{ mr: 1, color: '#1976d2', fontSize: '2rem' }} />
            <Typography variant="h6" color="#1976d2">Team Collaboration & Knowledge Sharing</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                  Documentation Standards
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Application runbooks"
                      secondary="Document each application's purpose, architecture, and dependencies"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Configuration guides"
                      secondary="Maintain setup and configuration documentation"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Troubleshooting guides"
                      secondary="Document common issues and their solutions"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#388e3c' }}>
                  Knowledge Transfer
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Regular training sessions"
                      secondary="Keep team members updated on AppConfig² features and best practices"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Pair programming"
                      secondary="Work together when making complex configuration changes"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Cross-training"
                      secondary="Ensure multiple team members can manage critical applications"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 152, 0, 0.05)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ef6c00' }}>
                  Change Management
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Approval workflows"
                      secondary="Require approval for production changes"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Change tracking"
                      secondary="Maintain logs of who made what changes and when"
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemText 
                      primary="Rollback procedures"
                      secondary="Have clear procedures for reverting problematic changes"
                    />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
            <AlertTitle>AppConfig² Advantage</AlertTitle>
            <Typography variant="body2">
              AppConfig² helps implement many of these best practices automatically with features like 
              built-in backup/restore, comprehensive testing tools, and detailed configuration visibility. 
              Use these capabilities alongside your organization's governance processes for optimal results.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Enhanced: Understanding Entra ID Concepts with Application Types */}
      <Card sx={{ borderRadius: 2, mb: 3, mt: 3, border: '2px solid #ff9800' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PolicyIcon sx={{ mr: 1, color: '#f57c00', fontSize: '1.5rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#f57c00' }}>
              Understanding App Roles vs API Permissions vs Scopes
            </Typography>
          </Box>
          
          <Typography variant="body1" paragraph color="text.primary">
            Understanding the distinction between <strong>App Roles</strong>, <strong>API Permissions</strong>, and <strong>Scopes</strong> is crucial for proper application configuration. 
            Each serves different purposes and is consumed by different types of applications.
          </Typography>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <GroupIcon sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  App Roles (Application-Specific Authorization)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>App Roles</strong> are custom roles defined within your application for internal authorization. 
                  They control what users can do <em>within your specific application</em>.
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Purpose: Internal User Authorization"
                      secondary="Control user capabilities within your application (e.g., 'Admin', 'Manager', 'ReadOnly')"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Appears in Tokens as 'roles' Claim"
                      secondary="Your application logic reads these roles to make authorization decisions"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Requires User/Group Assignment"
                      secondary="Administrators must explicitly assign users or groups to these roles"
                    />
                  </ListItem>
                </List>

                {/* Application Types that use App Roles */}
                <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                    💻 Applications That Define App Roles
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0' }}>APIs/Resource Servers (Most Common):</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        • REST APIs protecting endpoints<br />
                        • GraphQL APIs with role-based queries<br />
                        • Microservices with authorization<br />
                        • Business logic APIs<br />
                        • <strong>✅ Server-side role enforcement at API level</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0' }}>Web Applications (Confidential Clients):</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        • Line-of-business web apps<br />
                        • Enterprise portals<br />
                        • Admin dashboards<br />
                        • Internal business applications<br />
                        • <strong>✅ Server-side role enforcement</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                  
                  {/* Enhanced Security Pattern for Different App Types */}
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      🏗️ Common Architecture Pattern
                    </Typography>
                    <Typography variant="body2">
                      <strong>APIs/Web Apps define App Roles</strong> → <strong>SPAs/Mobile apps consume those roles</strong> → <strong>APIs/Web Apps enforce authorization server-side</strong><br/>
                      Example: Document API defines "DocumentAdmin", "DocumentEditor", "DocumentViewer" roles. Frontend apps (SPAs/Mobile) display appropriate UI, but the API validates roles in each request.
                    </Typography>
                  </Alert>

                  {/* Enhanced SPA Security Warning */}
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      🔒 SPAs and App Roles: Consumer Pattern Only
                    </Typography>
                    <Typography variant="body2">
                      <strong>SPAs should consume App Roles (defined by APIs/Web Apps) for user experience only:</strong><br/>
                      • ✅ Read roles from ID tokens to control UI elements<br/>
                      • ✅ Client-side route protection for better user experience<br/>
                      • ❌ Never rely on client-side role checks for actual security<br/>
                      • ✅ Backend APIs must always validate roles server-side for security enforcement
                    </Typography>
                  </Alert>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Example:</strong> An HR web application defines roles like "HR.Manager", "HR.Employee", "HR.Admin" to control 
                    who can view salaries, approve time-off, or manage employee records.
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ApiIcon sx={{ mr: 1, color: '#388e3c' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Scopes (API Resource Protection)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>Scopes</strong> are permissions that your API exposes to define what external applications can access. 
                  They follow OAuth 2.0 standards for resource protection.
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Purpose: API Resource Protection"
                      secondary="Define granular permissions for API operations (e.g., 'Documents.Read', 'Orders.Create')"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="OAuth 2.0 Standard Implementation"
                      secondary="Client applications request these scopes during OAuth flow"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="User Consent Required"
                      secondary="Users must consent when external applications request access to these scopes"
                    />
                  </ListItem>
                </List>

                {/* Application Types that expose/consume Scopes */}
                <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32' }}>
                    🔧 Applications That Define Scopes (APIs)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>Backend APIs:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        • REST APIs<br />
                        • GraphQL APIs<br />
                        • Microservices
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>Resource Servers:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        • Document management systems<br />
                        • File storage services<br />
                        • Business data APIs
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: '#2e7d32' }}>
                    📱 Applications That Request Scopes (Clients)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>Web Apps:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Server-side applications calling APIs on behalf of users
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>SPAs:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        JavaScript apps calling APIs directly from the browser
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>Mobile/Native:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        iOS/Android apps accessing backend services
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Example:</strong> A Document API exposes scopes like "Documents.Read", "Documents.Write", "Documents.Delete". 
                    A mobile app requests "Documents.Read" and "Documents.Write" to allow users to view and edit their documents.
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LockIcon sx={{ mr: 1, color: '#7b1fa2' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  API Permissions (External Resource Access)
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  <strong>API Permissions</strong> are permissions your application requests to access external APIs 
                  (like Microsoft Graph, custom APIs, or third-party services).
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#7b1fa2' }} fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Purpose: Access External Resources"
                      secondary="Request permission to call other APIs on behalf of users or as the application itself"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#7b1fa2' }} fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Two Types: Delegated & Application"
                      secondary="Delegated (on behalf of signed-in user) or Application (app-only, no user context)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon sx={{ color: '#7b1fa2' }} fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Admin Consent May Be Required"
                      secondary="High-privilege permissions require administrator approval before use"
                    />
                  </ListItem>
                </List>

                {/* Application Types that request API Permissions */}
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fce4ec', borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7b1fa2' }}>
                    🔌 Applications That Request API Permissions
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>Confidential Clients:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        • <strong>Web Applications:</strong> Server-side apps calling Microsoft Graph<br />
                        • <strong>Backend Services:</strong> Daemon apps running without user interaction<br />
                        • <strong>Integration Services:</strong> Automated workflows and sync tools
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>Public Clients:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        • <strong>Single-Page Apps:</strong> React/Angular apps calling Graph API<br />
                        • <strong>Mobile Apps:</strong> iOS/Android apps accessing user data<br />
                        • <strong>Desktop Apps:</strong> Native applications with user interaction
                      </Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1, color: '#7b1fa2' }}>
                    🎯 Common Permission Patterns by Client Type
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>User-Facing Apps:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Request <strong>delegated permissions</strong> like User.Read, Mail.Read, Calendars.ReadWrite
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>Background Services:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Request <strong>application permissions</strong> like User.Read.All, Mail.Read.All
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>Integration Apps:</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        Mix of both depending on whether user context is needed
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Example:</strong> A mobile expense app requests API permissions: "User.Read" (to get user profile), 
                    "Mail.Send" (to email receipts), and "Files.ReadWrite" (to access OneDrive for receipt storage).
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Application Architecture Examples */}
          <Box sx={{ mt: 3, p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1, color: '#666' }} />
              🏗️ Real-World Application Architecture Examples
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                    📊 Business Dashboard (SPA)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    <strong>Consumes:</strong> App Roles from backend APIs for UI control<br />
                    <strong>Requests:</strong> API permissions (User.Read, custom API scopes)<br />
                    <strong>Pattern:</strong> Client-side UX based on roles, server-side enforcement<br />
                    <strong>Note:</strong> Does not define its own App Roles
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#388e3c' }}>
                    🔧 Analytics API (Backend)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    <strong>Defines:</strong> App Roles ("AnalyticsAdmin", "DataAnalyst", "ReportViewer")<br />
                    <strong>Defines:</strong> Scopes ("Analytics.Read", "Reports.Generate")<br />
                    <strong>Requests:</strong> Application permissions (User.Read.All for user lookups)<br />
                    <strong>Enforces:</strong> Both role-based access control AND scope-based operations
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#7b1fa2' }}>
                    📱 Mobile App (Native)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 1 }}>
                    <strong>Requests:</strong> Delegated API permissions (User.Read, custom scopes)<br />
                    <strong>Uses:</strong> App Roles for in-app feature access<br />
                    <strong>Consumes:</strong> Multiple API scopes for different features
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Critical Naming Conflict Warning - Updated */}
          <Alert severity="error" sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ Critical: Naming Conflicts & Application Types
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Within the same application registration, you cannot have duplicate names across App Roles and Scopes.</strong>
              However, different application types can have the same permission names:
            </Typography>
            <List dense sx={{ mt: 1 }}>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary="✅ API App can expose scope 'Files.Read' AND Web App can define App Role 'Files.Read'"
                  secondary="Different applications = No conflict"
                />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary="❌ Same application cannot have both App Role 'Files.Read' AND Scope 'Files.Read'"
                  secondary="This creates internal naming conflicts and configuration errors"
                />
              </ListItem>
            </List>
          </Alert>

          {/* Enhanced Best Practices with Application Context */}
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              💡 Best Practices by Application Type
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>🖥️ Web/SPA Applications:</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  • <strong>App Roles:</strong> Use role names like "Admin", "Manager", "User"<br />
                  • <strong>API Permissions:</strong> Request minimal delegated permissions<br />
                  • <strong>Pattern:</strong> Focus on user-facing functionality and user context
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#388e3c', mb: 1 }}>🔧 API Applications:</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  • <strong>Scopes:</strong> Use action-based names like "Data.Read", "Orders.Create"<br />
                  • <strong>API Permissions:</strong> May need application permissions for lookups<br />
                  • <strong>Pattern:</strong> Focus on resource protection and granular access
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* NEW: AppConfig² Operational Best Practices */}
      <Card sx={{ borderRadius: 2, mb: 3, border: '2px solid #2196f3' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StorageIcon sx={{ mr: 1, color: '#1976d2', fontSize: '1.5rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
              AppConfig² Cache Management & Workflow Best Practices
            </Typography>
          </Box>
          
          <Typography variant="body1" paragraph color="text.primary">
            AppConfig² uses intelligent caching to provide <strong>fast application loading</strong> and <strong>improved performance</strong>. 
            Understanding how this works with external changes is important for optimal usage.
          </Typography>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CachedIcon sx={{ mr: 1, color: '#1976d2' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  How AppConfig² Caching Works
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  AppConfig² caches application data locally for <strong>5 minutes</strong> to provide fast performance and reduce Microsoft Graph API calls.
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Fast Application Loading"
                      secondary="Cached data loads in ~200ms vs 3-8 seconds for fresh data"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Automatic Cache Management"
                      secondary="Cache automatically refreshes every 5 minutes or when you sign out/in"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText
                      primary="Secure Cache Clearing"
                      secondary="All cached data is automatically cleared when you sign out"
                    />
                  </ListItem>
                </List>

                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Performance Benefit:</strong> After the initial load, navigating between Applications and other sections is instant thanks to intelligent caching.
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <SyncIcon sx={{ mr: 1, color: '#f57c00' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Working with External Changes
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  If you make changes directly in <strong>Microsoft Entra ID portal</strong> (outside of AppConfig²), those changes won't immediately appear in AppConfig² due to caching.
                </Typography>
                
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#f57c00' }}>
                  📋 Best Practice Workflows
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #4caf50' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
                        <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: '1rem' }} />
                        Recommended: Use AppConfig² Only 
                      </Typography>
                      <List dense>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText 
                            primary="🎯 When testing apps, make all changes in AppConfig²"
                            secondary="Changes are immediately reflected in the interface"
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText 
                            primary="⚡ Instant updates"
                            secondary="No cache synchronization issues"
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText 
                            primary="🔄 Consistent experience"
                            secondary="All changes tracked and visible immediately"
                          />
                        </ListItem>
                      </List>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid #ff9800' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#f57c00', display: 'flex', alignItems: 'center' }}>
                        <LogoutIcon sx={{ mr: 1, fontSize: '1rem' }} />
                        If Using Entra ID Portal
                      </Typography>
                      <List dense>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText 
                            primary="1️⃣ Make changes in Entra ID portal"
                            secondary="Complete all your external changes first"
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText 
                            primary="2️⃣ Sign out of AppConfig²"
                            secondary="This clears all cached data"
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText 
                            primary="3️⃣ Sign back in to AppConfig²"
                            secondary="Fresh data will be loaded from Entra ID"
                          />
                        </ListItem>
                      </List>
                    </Box>
                  </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    💡 Why Sign Out/Sign In Works
                  </Typography>
                  <Typography variant="body2">
                    Signing out completely clears all cached application data from your browser. When you sign back in, 
                    AppConfig² loads fresh data directly from Microsoft Graph, ensuring you see all recent changes.
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoOutlinedIcon sx={{ mr: 1, color: '#7b1fa2' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Cache Management Examples
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2" paragraph>
                  Here are common scenarios and how to handle them:
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7b1fa2' }}>
                        Scenario 1: Adding App Roles
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        ✅ <strong>In AppConfig²:</strong> Use App Roles section → Changes appear immediately<br/>
                        ⚠️ <strong>In Entra portal:</strong> Add roles → Sign out/in AppConfig² to see them
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7b1fa2' }}>
                        Scenario 2: API Permissions
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        ✅ <strong>In AppConfig²:</strong> Use API Permissions section → Instant updates<br/>
                        ⚠️ <strong>In Entra portal:</strong> Grant permissions → Sign out/in to refresh
                      </Typography>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ p: 2, bgcolor: '#f3e5f5', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7b1fa2' }}>
                        Scenario 3: Optional Claims
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        ✅ <strong>In AppConfig²:</strong> Use Optional Claims → See changes right away<br/>
                        ⚠️ <strong>In Entra portal:</strong> Configure claims → Sign out/in for sync
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    ⏱️ Cache Duration: 5 Minutes
                  </Typography>
                  <Typography variant="body2">
                    Even without signing out, cached data automatically expires after 5 minutes. However, for immediate 
                    visibility of external changes, the sign out/sign in approach is most reliable.
                  </Typography>
                </Alert>
              </Box>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      {/* Team Collaboration section continues here... */}

      {/* Information Note */}
      <Paper elevation={0} sx={{ p: 3, mt: 4, backgroundColor: '#e8f5e8', borderRadius: 2, border: '1px solid', borderColor: 'success.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <InfoOutlinedIcon color="success" sx={{ mr: 1 }} />
          <Typography variant="h6" color="success.dark" sx={{ fontWeight: 500 }}>
            Implementing Best Practices
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          These best practices are recommendations based on enterprise deployments and Microsoft guidance. 
          Start with the security fundamentals and gradually implement additional practices based on your 
          organization's maturity and requirements. Regular reviews and updates ensure continued effectiveness.
        </Typography>
      </Paper>
    </Box>
  );
};

export default BestPracticesTab;